import React, { useEffect, useState, useRef, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { HeadphonesIcon, ChevronRightIcon, FilterIcon, ChevronDownIcon, SearchIcon, ArrowLeftIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import AudiobookCard from '../components/Books/AudiobookCard';
import { audiobooks as realAudiobooks } from '../AudioBookDetails';
// Genre mapping configuration
const genreConfig: Record<string, {
  name: string;
  color: string;
  image: string;
  description: string;
}> = {
  romance: {
    name: 'Romance',
    color: 'from-pink-600 to-rose-600',
    image: 'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=1200',
    description: 'Fall in love with our collection of swoon-worthy romance audiobooks, from sweet contemporary tales to dark and spicy sagas.'
  },
  fantasy: {
    name: 'Fantasy',
    color: 'from-indigo-600 to-violet-600',
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200',
    description: 'Escape to magical worlds filled with dragons, fae, and epic adventures brought to life by incredible narrators.'
  },
  'sci-fi': {
    name: 'Science Fiction',
    color: 'from-blue-600 to-cyan-600',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200',
    description: 'Explore the cosmos and future technologies with our selection of mind-bending sci-fi audiobooks.'
  },
  mystery: {
    name: 'Mystery',
    color: 'from-slate-600 to-gray-600',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200',
    description: 'Unravel complex puzzles and whodunits that will keep you guessing until the very last minute.'
  },
  thriller: {
    name: 'Thriller',
    color: 'from-red-600 to-orange-600',
    image: 'https://images.unsplash.com/photo-1509281373149-e957c6296406?w=1200',
    description: 'Heart-pounding suspense and edge-of-your-seat action that demands to be heard.'
  },
  horror: {
    name: 'Horror',
    color: 'from-gray-900 to-black',
    image: 'https://images.unsplash.com/photo-1509557965875-b88c97052f0e?w=1200',
    description: 'Chilling tales designed to keep you up at night. Listen with the lights on.'
  },
  'historical-fiction': {
    name: 'Historical Fiction',
    color: 'from-amber-700 to-yellow-600',
    image: 'https://images.unsplash.com/photo-1461360370896-922624d12a74?w=1200',
    description: 'Travel back in time with immersive stories set against the backdrop of history.'
  },
  contemporary: {
    name: 'Contemporary',
    color: 'from-teal-600 to-emerald-600',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200',
    description: 'Modern stories reflecting our current world, relationships, and challenges.'
  }
};
// Additional mock data to populate the pages
const mockAudiobooks = [
// Romance
{
  title: "The Heart's Equation",
  author: 'Elena Armas',
  coverImage: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&h=600&fit=crop',
  narrators: ['Callie Dalton', 'Teddy Hamilton'],
  audioLength: '11h 20m',
  genre: 'Romance',
  subgenres: ['Contemporary', 'RomCom'],
  relationshipTypes: ['MF'],
  audioSampleUrl: '',
  spiceLevel: 3,
  tropes: ['Enemies to Lovers', 'Workplace'],
  series: {
    name: 'Love Science',
    number: 1,
    seriesComplete: false
  }
}, {
  title: 'Midnight Vows',
  author: 'Sophie Lark',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=600&fit=crop',
  narrators: ['Shane East', 'Stella Hunter'],
  audioLength: '9h 45m',
  genre: 'Romance',
  subgenres: ['Dark Romance', 'Mafia'],
  relationshipTypes: ['MF'],
  audioSampleUrl: '',
  spiceLevel: 4,
  tropes: ['Arranged Marriage', 'Mafia'],
  series: {
    name: 'Underworld Kings',
    number: 2,
    seriesComplete: true
  }
}, {
  title: 'Summer at the Lake',
  author: 'Carley Fortune',
  coverImage: 'https://images.unsplash.com/photo-1476275466078-4007374efbbe?w=400&h=600&fit=crop',
  narrators: ['A.J. Cook'],
  audioLength: '10h 15m',
  genre: 'Romance',
  subgenres: ['Contemporary', 'Second Chance'],
  relationshipTypes: ['MF'],
  audioSampleUrl: '',
  spiceLevel: 2,
  tropes: ['Second Chance', 'Small Town'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
},
// Fantasy
{
  title: 'Crown of Shadows',
  author: 'K.F. Breene',
  coverImage: 'https://images.unsplash.com/photo-1535905557558-afc4877a26fc?w=400&h=600&fit=crop',
  narrators: ['Nicole Poole'],
  audioLength: '14h 30m',
  genre: 'Fantasy',
  subgenres: ['Urban Fantasy', 'Paranormal'],
  relationshipTypes: ['Why Choose'],
  audioSampleUrl: '',
  spiceLevel: 3,
  tropes: ['Magic Academy', 'Hidden Power'],
  series: {
    name: 'Shadow Realm',
    number: 1,
    seriesComplete: false
  }
}, {
  title: "The Dragon's Mate",
  author: 'Ruby Dixon',
  coverImage: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=400&h=600&fit=crop',
  narrators: ['Noelle Bridges'],
  audioLength: '8h 50m',
  genre: 'Fantasy',
  subgenres: ['Romantasy', 'Paranormal'],
  relationshipTypes: ['MF'],
  audioSampleUrl: '',
  spiceLevel: 4,
  tropes: ['Fated Mates', 'Dragon Shifter'],
  series: {
    name: 'Fireblood Dragons',
    number: 3,
    seriesComplete: false
  }
}, {
  title: 'Court of Thorns',
  author: 'Sarah J. Maas',
  coverImage: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=400&h=600&fit=crop',
  narrators: ['Jennifer Ikeda'],
  audioLength: '16h 10m',
  genre: 'Fantasy',
  subgenres: ['High Fantasy', 'Romantasy'],
  relationshipTypes: ['MF'],
  audioSampleUrl: '',
  spiceLevel: 3,
  tropes: ['Enemies to Lovers', 'Fae'],
  series: {
    name: 'Thorns and Roses',
    number: 1,
    seriesComplete: false
  }
},
// Sci-Fi
{
  title: "Starship's Mage",
  author: 'Glynn Stewart',
  coverImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=600&fit=crop',
  narrators: ['Jeffrey Kafer'],
  audioLength: '12h 45m',
  genre: 'Science Fiction',
  subgenres: ['Space Opera', 'Fantasy'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 1,
  tropes: ['Space Magic', 'Underdog'],
  series: {
    name: "Starship's Mage",
    number: 1,
    seriesComplete: true
  }
}, {
  title: 'Project Hail Mary',
  author: 'Andy Weir',
  coverImage: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=400&h=600&fit=crop',
  narrators: ['Ray Porter'],
  audioLength: '16h 10m',
  genre: 'Science Fiction',
  subgenres: ['Hard Sci-Fi'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 0,
  tropes: ['Survival', 'First Contact'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
}, {
  title: 'Dune',
  author: 'Frank Herbert',
  coverImage: 'https://images.unsplash.com/photo-1547234935-80c7145ec969?w=400&h=600&fit=crop',
  narrators: ['Scott Brick', 'Orlagh Cassidy'],
  audioLength: '21h 02m',
  genre: 'Science Fiction',
  subgenres: ['Epic Sci-Fi'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 1,
  tropes: ['Chosen One', 'Political Intrigue'],
  series: {
    name: 'Dune Chronicles',
    number: 1,
    seriesComplete: true
  }
},
// Mystery/Thriller
{
  title: 'The Silent Patient',
  author: 'Alex Michaelides',
  coverImage: 'https://images.unsplash.com/photo-1587876931567-564ce588bfbd?w=400&h=600&fit=crop',
  narrators: ['Jack Hawkins', 'Louise Brealey'],
  audioLength: '8h 43m',
  genre: 'Thriller',
  subgenres: ['Psychological Thriller'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 1,
  tropes: ['Unreliable Narrator', 'Twist Ending'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
}, {
  title: 'Gone Girl',
  author: 'Gillian Flynn',
  coverImage: 'https://images.unsplash.com/photo-1495446815901-a7297e633e8d?w=400&h=600&fit=crop',
  narrators: ['Julia Whelan', 'Kirby Heyborne'],
  audioLength: '19h 11m',
  genre: 'Mystery',
  subgenres: ['Thriller', 'Crime'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 2,
  tropes: ['Missing Person', 'Dark Secrets'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
}, {
  title: 'The Girl on the Train',
  author: 'Paula Hawkins',
  coverImage: 'https://images.unsplash.com/photo-1474366521946-c3d4b507abf2?w=400&h=600&fit=crop',
  narrators: ['Clare Corbett', 'Louise Brealey'],
  audioLength: '10h 59m',
  genre: 'Thriller',
  subgenres: ['Mystery', 'Suspense'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 1,
  tropes: ['Unreliable Narrator', 'Witness'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
},
// Historical Fiction
{
  title: 'The Nightingale',
  author: 'Kristin Hannah',
  coverImage: 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=400&h=600&fit=crop',
  narrators: ['Polly Stone'],
  audioLength: '17h 19m',
  genre: 'Historical Fiction',
  subgenres: ['WWII'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 1,
  tropes: ['Sisters', 'War'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
}, {
  title: 'Bridgerton: The Duke and I',
  author: 'Julia Quinn',
  coverImage: 'https://images.unsplash.com/photo-1524578271613-d550eacf6090?w=400&h=600&fit=crop',
  narrators: ['Rosalyn Landor'],
  audioLength: '12h 15m',
  genre: 'Romance',
  subgenres: ['Historical Romance', 'Regency'],
  relationshipTypes: ['MF'],
  audioSampleUrl: '',
  spiceLevel: 3,
  tropes: ['Fake Dating', 'Regency'],
  series: {
    name: 'Bridgerton',
    number: 1,
    seriesComplete: false
  }
},
// Horror
{
  title: 'It',
  author: 'Stephen King',
  coverImage: 'https://images.unsplash.com/photo-1509557965875-b88c97052f0e?w=400&h=600&fit=crop',
  narrators: ['Steven Weber'],
  audioLength: '44h 57m',
  genre: 'Horror',
  subgenres: ['Supernatural'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 0,
  tropes: ['Small Town Horror', 'Monster'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
}, {
  title: 'The Haunting of Hill House',
  author: 'Shirley Jackson',
  coverImage: 'https://images.unsplash.com/photo-1481018085669-2bc6e4f00eed?w=400&h=600&fit=crop',
  narrators: ['Bernadette Dunne'],
  audioLength: '7h 12m',
  genre: 'Horror',
  subgenres: ['Gothic', 'Psychological'],
  relationshipTypes: [],
  audioSampleUrl: '',
  spiceLevel: 0,
  tropes: ['Haunted House', 'Madness'],
  series: {
    name: '',
    number: 1,
    seriesComplete: true
  }
}];
// Combine real and mock data
const allAudiobooks = [...realAudiobooks, ...mockAudiobooks];
const AudiobookGenrePage = () => {
  const {
    genre
  } = useParams<{
    genre: string;
  }>();
  const dk = useDarkClasses();
  const navigate = useNavigate();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const audioRefs = useRef<{
    [key: string]: HTMLAudioElement;
  }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubgenre, setSelectedSubgenre] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'title'>('popular');
  const [showFilters, setShowFilters] = useState(false);
  // Get genre config or default
  const currentGenre = genre && genreConfig[genre] ? genreConfig[genre] : null;
  // Redirect if genre not found
  useEffect(() => {
    if (genre && !genreConfig[genre]) {
      navigate('/audiobooks');
    }
  }, [genre, navigate]);
  if (!currentGenre) return null;
  // Filter books by genre
  const genreBooks = allAudiobooks.filter(book => {
    const mainGenreMatch = book.genre.toLowerCase() === currentGenre.name.toLowerCase();
    const subGenreMatch = book.subgenres && book.subgenres.some(sg => sg.toLowerCase() === currentGenre.name.toLowerCase());
    // Special case for "Historical Fiction" matching "Historical" slug
    const historicalMatch = genre === 'historical-fiction' && (book.genre === 'Historical Fiction' || book.subgenres && book.subgenres.includes('Historical Fiction'));
    // Special case for "Contemporary" matching "Contemporary" slug
    const contemporaryMatch = genre === 'contemporary' && book.subgenres && book.subgenres.includes('Contemporary');
    return mainGenreMatch || subGenreMatch || historicalMatch || contemporaryMatch;
  });
  // Get unique subgenres for filter
  const subgenres = ['All', ...Array.from(new Set(genreBooks.flatMap(b => b.subgenres || [])))].sort();
  // Filter by search and subgenre
  const filteredBooks = genreBooks.filter(book => {
    const matchesSearch = book.title.toLowerCase().includes(searchQuery.toLowerCase()) || book.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSubgenre = selectedSubgenre === 'All' || book.subgenres && book.subgenres.includes(selectedSubgenre);
    return matchesSearch && matchesSubgenre;
  });
  // Sort books
  const sortedBooks = [...filteredBooks].sort((a, b) => {
    if (sortBy === 'title') return a.title.localeCompare(b.title);
    // Mock sorting for others since we don't have real dates/popularity metrics in this simple data
    if (sortBy === 'newest') return 0.5 - Math.random();
    return 0; // default order (popularity mock)
  });
  // Audio playback handlers
  const handlePlayPause = (bookId: string) => {
    if (currentlyPlaying === bookId) {
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].pause();
      }
      setCurrentlyPlaying(null);
    } else {
      if (currentlyPlaying && audioRefs.current[currentlyPlaying]) {
        audioRefs.current[currentlyPlaying].pause();
      }
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].play();
        setCurrentlyPlaying(bookId);
      }
    }
  };
  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
  };
  return <div className={`min-h-screen ${dk.isDark ? 'bg-gray-900' : 'bg-gray-50'}`} data-id="element-5706" data-mp="3t1ybvhsi23yr">
      {/* Hero Banner */}
      <div className="relative h-64 md:h-80 overflow-hidden" data-id="element-5707" data-mp="2lq7o114kmfr0">
        <div className="absolute inset-0" data-id="element-5708" data-mp="2ho2v64ly5n5f">
          <img src={currentGenre.image} alt={currentGenre.name} className="w-full h-full object-cover" data-id="element-5709" data-mp="2hn1hdsdjqmk0" />
          <div className={`absolute inset-0 bg-gradient-to-r ${currentGenre.color} opacity-80 mix-blend-multiply`} data-id="element-5710" data-mp="11747uz9x5ekd"></div>
          <div className="absolute inset-0 bg-black/30" data-id="element-5711" data-mp="165oyq83a6lw5"></div>
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center" data-id="element-5712" data-mp="1axgowhiiyplr">
          <div className="mb-4 flex items-center text-white/80 text-sm font-medium" data-id="element-5713" data-mp="1fv8hzeuwohrj">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-5714" data-mp="1kuleysfhr57u">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5715" data-mp="1oy1aflftqziy" />
            <Link to="/audiobooks" className="hover:text-white transition-colors" data-id="element-5716" data-mp="1tw2qdxhyeiq7">
              Audiobooks
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5717" data-mp="3qq5w4f6qd0l8" />
            <span className="text-white" data-id="element-5718" data-mp="3vmwe5823i69a">{currentGenre.name}</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center" data-id="element-5719" data-mp="12so03tm5wqm7">
            <HeadphonesIcon className="h-10 w-10 md:h-12 md:w-12 mr-4 opacity-90" data-id="element-5720" data-mp="xtuo65mix7o5" />
            {currentGenre.name} Audiobooks
          </h1>

          <p className="text-lg text-white/90 max-w-2xl leading-relaxed" data-id="element-5721" data-mp="1buf50w8votf0">
            {currentGenre.description}
          </p>

          <div className="mt-6 flex items-center gap-4" data-id="element-5722" data-mp="16t7zl3u7k6bi">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 text-white text-sm backdrop-blur-sm border border-white/30" data-id="element-5723" data-mp="1lr0dxso0qoo4">
              {genreBooks.length} Titles Available
            </span>
          </div>
        </div>
      </div>

      {/* Filters & Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5724" data-mp="1qi9y4dqsszsm">
        {/* Controls Bar */}
        <div className={`sticky top-16 z-20 -mx-4 px-4 py-4 mb-8 border-b ${dk.isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-gray-50/95 border-gray-200'} backdrop-blur-sm transition-all`} data-id="element-5725" data-mp="24hi0tgm66q1k">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-5726" data-mp="2j61k2tfyipag">
            {/* Search */}
            <div className="relative flex-1 max-w-md" data-id="element-5727" data-mp="2o3kybtcnn22e">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5728" data-mp="297c10xcbqysm" />
              <input type="text" placeholder={`Search ${currentGenre.name} audiobooks...`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${dk.isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`} data-id="element-5729" data-mp="2e7qbem1eektt" />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 overflow-x-auto pb-2 md:pb-0 hide-scrollbar" data-id="element-5730" data-mp="1lfy8aqa9h174">
              <div className="flex items-center gap-2" data-id="element-5731" data-mp="21vfsi8864uyw">
                <span className={`text-sm font-medium whitespace-nowrap ${dk.isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5732" data-mp="1wxnyn8nyqys2">
                  Subgenre:
                </span>
                <select value={selectedSubgenre} onChange={e => setSelectedSubgenre(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 ${dk.isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-5733" data-mp="1mzqb6ndxh60i">
                  {subgenres.map(sg => <option key={sg} value={sg} data-id="element-5734" data-mp="149if2p8nbftt">
                      {sg}
                    </option>)}
                </select>
              </div>

              <div className="flex items-center gap-2" data-id="element-5735" data-mp="zbqbuz2jw7ab">
                <span className={`text-sm font-medium whitespace-nowrap ${dk.isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5736" data-mp="v9lm4bf83usy">
                  Sort by:
                </span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} className={`rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 ${dk.isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-5737" data-mp="3avo4mtek7w5c">
                  <option value="popular" data-id="element-5738" data-mp="2s6hw1z7hckx9">Popular</option>
                  <option value="newest" data-id="element-5739" data-mp="2s712ainrpkvg">Newest</option>
                  <option value="title" data-id="element-5740" data-mp="2x5cbqsa5htd5">Title (A-Z)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Books Grid */}
        {sortedBooks.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-5741" data-mp="24g5fjzy65fv2">
            {sortedBooks.map((book, index) => <div key={`${book.title}-${index}`} className="flex justify-center" data-id="element-5742" data-mp="29dx5iof96rwm" data-mpi={index}>
                <div className="w-full max-w-[260px]" data-id="element-5743" data-mp="1mce2leyd741z">
                  <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl || ''} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} data-id="element-5744" data-mp="1mb2r0syogs0b" />
                  {/* Hidden audio element for playback */}
                  <audio ref={el => {
              if (el) audioRefs.current[book.title] = el;
            }} src={book.audioSampleUrl} onEnded={handleAudioEnded} className="hidden" data-id="element-5745" data-mp="2p7m9kre1yjm9" />
                </div>
              </div>)}
          </div> : <div className="text-center py-20" data-id="element-5746" data-mp="26f9xve1ie5zg">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${dk.isDark ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-5747" data-mp="21g70mfrrt421">
              <HeadphonesIcon className="h-8 w-8" data-id="element-5748" data-mp="1rh968w7nygb4" />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5749" data-mp="1rk3qonqog14j">
              No audiobooks found
            </h3>
            <p className={`max-w-md mx-auto ${dk.isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5750" data-mp="1mut62h2pa4oc">
              We couldn't find any audiobooks matching your search criteria. Try
              adjusting your filters or search term.
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSelectedSubgenre('All');
        }} className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors" data-id="element-5751" data-mp="20lo9hdufjtkj">
              Clear Filters
            </button>
          </div>}

        {/* Related Genres */}
        <div className="mt-20 pt-10 border-t border-gray-200 dark:border-gray-800" data-id="element-5752" data-mp="2jiz6sswi4cxc">
          <h2 className={`text-2xl font-bold mb-6 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5753" data-mp="2o9z4i0dg2z0w">
            Browse Other Genres
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-5754" data-mp="ct5nppl57a73">
            {Object.entries(genreConfig).filter(([key]) => key !== genre).slice(0, 4).map(([key, config]) => <Link key={key} to={`/audiobooks/${key}`} className="group relative h-24 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all" data-id="element-5755" data-mp="hlyvkmbwqyne">
                  <div className="absolute inset-0" data-id="element-5756" data-mp="3l3qr2ap960qc">
                    <img src={config.image} alt={config.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-5757" data-mp="3l51vmcqivcii" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-70 group-hover:opacity-60 transition-opacity`} data-id="element-5758" data-mp="1f8o1365tjte0"></div>
                  </div>
                  <div className="absolute inset-0 p-4 flex items-center justify-center" data-id="element-5759" data-mp="3g07zxko05s3c">
                    <h3 className="text-white font-bold text-lg text-center shadow-black drop-shadow-md" data-id="element-5760" data-mp="3kz17yxkisqw4">
                      {config.name}
                    </h3>
                  </div>
                </Link>)}
          </div>
        </div>
      </div>
    </div>;
};
export default AudiobookGenrePage;